import { TextInput as BaseTextInput, Button, FlexColumn, FlexMiddle, Header2 } from '@ubeya/shared-web/components';
import { AccordionSummaryContainer } from '@ubeya/shared-web/components/Accordion/Accordion';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import withPermissions from '@ubeya/shared-web/hoc/withPermissions';
import {
  ACCOUNTS_CONTRACTS_TYPES,
  ContractTypesWithMinimums,
  CONTRACT_TYPES_WITH_TOIL_ACCRUAL
} from '@ubeya/shared/constants';
import moment from 'moment';
import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as BaseEditIcon } from '@ubeya/shared-web/assets/edit.svg';
import privileges from '@ubeya/shared-web/privileges';
import EmployeeContractTerminateModal from './EmployeeContractTerminateModal';
import AdjustEmployeeContractModal from './AdjustEmployeeContractModal';
import AdjustEmployeeContractStartDateModal from './AdjustEmployeeContractStartDateModal';

const TextInput = styled(BaseTextInput)`
  width: calc(50% - 8px);
  min-width: 250px;
  input:disabled {
    color: ${({ theme }) => theme.colors.grey3};
    border: 1px solid ${({ theme }) => theme.colors.grey2};
    border-radius: 4px;
    background: ${({ theme }) => theme.colors.white};
  }
`;
const RowContainer = styled(FlexMiddle)`
  gap: 8px;
  flex-wrap: wrap;
`;
const RowContainerEnd = styled(RowContainer)`
  align-items: flex-end;
`;

const Container = styled(FlexColumn)`
  gap: 4px;
  /* width: calc(50% - 8px); */
  width: 100%;
`;
const Label = styled.label`
  color: ${({ theme }) => theme.colors.grey4};
`;
const Box = styled(FlexMiddle)`
  cursor: ${({ $isEdit }) => ($isEdit ? 'pointer' : 'default')};
  justify-content: space-between;
  border: 1px solid ${({ theme }) => theme.colors.disabled};
  border-radius: 4px;
  padding: 10px;
`;

const EditIcon = styled(BaseEditIcon)`
  path {
    fill: ${({ theme }) => theme.colors.primary};
  }
`;
const AdjustButton = styled(Button)`
  background: none;
  display: flex;
  align-items: center;
  gap: 4px;
  height: 32px;

  &:hover,
  &:focus {
    background: none !important;
  }
`;

const ActiveContract = ({
  activeContract,
  terminateEmployeeContract,
  adjustEmployeeContract,
  userPermissions: { hasActionPermission }
}) => {
  const { t, currency, dateFormat } = useContext(ConfigContext);

  const [showTerminateModal, setShowTerminateModal] = useState(false);
  const [showAdjustHolidayAccrualModal, setShowAdjustHolidayAccrualModal] = useState(false);
  const [showAdjustEmployeeStartDateModal, setShowAdjustEmployeeStartDateModal] = useState(false);

  if (!activeContract) return <></>;

  const formattedStartDate = moment(activeContract.employeeStartDate).format(dateFormat);
  const minimumWeeklyWorkingHours =
    activeContract.employeeMinimumWeeklyWorkingHours || activeContract.minimumWeeklyWorkingHours;
  const minimumHourlyRateOfPay = activeContract.employeeMinimumHourlyRateOfPay || activeContract.minimumHourlyRateOfPay;
  const maxDaysToConsumeInWeek = activeContract.employeeMaxDaysConsumeInWeek || activeContract.maxDaysConsumeInWeek;

  const holidayAccrualYearlyEntitlement =
    (activeContract.employeeHolidayAccrualYearlyEntitlement || activeContract.holidayAccrualYearlyEntitlement || 0) +
    (activeContract.holidayAccrualYearlyEntitlementAdjustment || 0);
  const sickDaysYearlyEntitlement =
    (activeContract.employeeSickDaysYearlyEntitlement || activeContract.sickDaysYearlyEntitlement || 0) +
    (activeContract.sickDaysYearlyEntitlementAdjustment || 0);

  const shouldViewMinimumOptions = ContractTypesWithMinimums.includes(activeContract.type);
  const shouldViewToilFields = CONTRACT_TYPES_WITH_TOIL_ACCRUAL.includes(activeContract.type);
  const isCasualContract = activeContract.type === ACCOUNTS_CONTRACTS_TYPES.Casual;

  const employmentRate = activeContract.employmentRate;
  const hoursPerWorkday = activeContract.employeeHoursPerWorkday ?? activeContract.hoursPerWorkday;

  return (
    <>
      <RowContainerEnd>
        <Container>
          <Label>{t('contractTypes_employeeContractStartingDate')}</Label>
          <Box
            $isEdit={hasActionPermission}
            onClick={() => hasActionPermission && setShowAdjustEmployeeStartDateModal(true)}>
            <p>{formattedStartDate}</p>
            {hasActionPermission && <BaseEditIcon />}
          </Box>
        </Container>

        {/* <Tooltip
          body={t('missingPrivilege', {
            privilege: t('adminPrivilegesPermissionEditContractType')
          })}
          disabled={hasActionPermission}>
          <TerminateButton
            secondaryGray
            type="reset"
            disabled={!hasActionPermission}
            onClick={() => hasActionPermission && setShowTerminateModal(true)}>
            {t('contractTypes_terminateEmployeeContract')}
          </TerminateButton>
        </Tooltip> */}
      </RowContainerEnd>

      {!isCasualContract && (
        <>
          <AccordionSummaryContainer>
            <Header2> {t('contractTypes_employeeSpecificContractTerms')} </Header2>
            {hasActionPermission && (
              <AdjustButton type="button" secondary onClick={() => setShowAdjustHolidayAccrualModal(true)}>
                <EditIcon />
                {t('edit')}
              </AdjustButton>
            )}
          </AccordionSummaryContainer>
          {shouldViewMinimumOptions && (
            <RowContainer>
              <TextInput
                title={t('contractTypes_minimumWeeklyWorkingHours')}
                value={minimumWeeklyWorkingHours}
                disabled
              />
              <TextInput
                title={t('contractTypes_minimumHourlyRateOfPay')}
                value={minimumHourlyRateOfPay !== null ? `${minimumHourlyRateOfPay}${currency}` : ''}
                disabled
              />
            </RowContainer>
          )}

          <RowContainer>
            <TextInput
              title={t('contractTypes_holidayAccrualYearlyEntitlement')}
              value={holidayAccrualYearlyEntitlement}
              disabled
            />

            {maxDaysToConsumeInWeek && (
              <TextInput title={t('contractTypes_maxDaysToConsumeInAWeek')} value={maxDaysToConsumeInWeek} disabled />
            )}
          </RowContainer>

          <RowContainer>
            <TextInput
              title={t('contractTypes_sickDaysYearlyEntitlement')}
              value={sickDaysYearlyEntitlement}
              disabled
            />
          </RowContainer>

          {shouldViewToilFields && (
            <RowContainer>
              <TextInput title={t('contractTypes_employmentRate')} value={employmentRate ?? '100'} disabled />
              <TextInput title={t('contractTypes_hoursPerWorkday')} value={hoursPerWorkday ?? '8'} disabled />
            </RowContainer>
          )}
        </>
      )}

      {showTerminateModal && (
        <EmployeeContractTerminateModal
          onClose={() => setShowTerminateModal(false)}
          employeeContractId={activeContract.employeeContractId}
          terminateEmployeeContract={terminateEmployeeContract}
        />
      )}

      {showAdjustHolidayAccrualModal && (
        <AdjustEmployeeContractModal
          contract={activeContract}
          onClose={() => setShowAdjustHolidayAccrualModal(false)}
          adjustEmployeeContract={adjustEmployeeContract}
        />
      )}
      {showAdjustEmployeeStartDateModal && (
        <AdjustEmployeeContractStartDateModal
          contract={activeContract}
          onClose={() => setShowAdjustEmployeeStartDateModal(false)}
          adjustEmployeeContract={adjustEmployeeContract}
        />
      )}
    </>
  );
};

export default withPermissions(React.memo(ActiveContract), {
  action: { privileges: [privileges.MANAGE_EMPLOYEE_HOLIDAY_ACCRUAL] }
});
